import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { Seitenkopf } from "@/components/entwurf/Seitenkopf";
import { Leerzustand } from "@/components/Leerzustand";
import { Card } from "@/components/Card";
import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { projektfarbe } from "@/lib/projektfarbe";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import {
  fehlerMeldung,
  useIch,
  useProjektAnlegen,
  useProjekte,
} from "@/lib/haken";
import { darfAktion } from "@/lib/rollen";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import type { Rolle } from "@cubic/shared";

export function ProjekteListe() {
  const ich = useIch();
  const projekte = useProjekte();
  const anlegen = useProjektAnlegen();
  const navigate = useNavigate();
  const [q, setzeQ] = useState("");
  const [dialogOffen, setzeDialog] = useState(false);
  const [kuerzel, setzeKuerzel] = useState("");
  const [name, setzeName] = useState("");
  const [fehler, setzeFehler] = useState<string | null>(null);

  const rollen = useMemo(() => {
    const m = new Map<string, Rolle>();
    for (const p of ich.data?.projekte ?? []) {
      m.set(p.projektId, p.rolle as Rolle);
    }
    return m;
  }, [ich.data?.projekte]);

  const darfAnlegen = darfAktion({
    aktion: "projekt_anlegen",
    rechte: ich.data?.rechte,
  });

  const gefiltert = useMemo(() => {
    const liste = projekte.data ?? [];
    if (!q.trim()) return liste;
    const n = q.toLowerCase();
    return liste.filter(
      (p) =>
        p.name.toLowerCase().includes(n) ||
        p.kuerzel.toLowerCase().includes(n),
    );
  }, [projekte.data, q]);

  async function speichern() {
    setzeFehler(null);
    try {
      const p = await anlegen.mutateAsync({
        kuerzel: kuerzel.trim().toUpperCase(),
        name: name.trim(),
      });
      setzeDialog(false);
      setzeKuerzel("");
      setzeName("");
      void navigate({
        to: "/projekte/$id",
        params: { id: p.id },
        search: { reiter: "aufgaben" },
      });
    } catch (e) {
      setzeFehler(fehlerMeldung(e).text);
    }
  }

  if (projekte.isLoading) {
    return <p className="text-sm text-gray-500">Lade Projekte …</p>;
  }

  const leerArt =
    (projekte.data?.length ?? 0) === 0
      ? "nichts"
      : gefiltert.length === 0
        ? "filter"
        : null;

  return (
    <>
      {/*
        Vorher der alte `components/Seitenkopf` aus der Tremor-Vorlage: Titel in
        22px fett, ohne Kontextzeile. Damit begann die Überschrift 38px höher und
        in einem anderen Grad als auf jeder anderen Seite.
      */}
      <Seitenkopf
        kontext="Kacheln deiner sichtbaren Projekte"
        aktion={
          darfAnlegen ? (
            <Button onClick={() => setzeDialog(true)} data-testid="projekt-neu">
              Neues Projekt
            </Button>
          ) : null
        }
      >
        Projekte
      </Seitenkopf>

      <div className="max-w-sm">
        <Input
          placeholder="Suche …"
          value={q}
          onChange={(e) => setzeQ(e.target.value)}
          aria-label="Projekte filtern"
        />
      </div>

      {leerArt === "nichts" ? (
        <Leerzustand
          titel="Noch kein Projekt."
          hinweis="Lege eins an — dann können Aufgaben folgen."
          aktionLabel={darfAnlegen ? "Projekt anlegen" : undefined}
          onAktion={darfAnlegen ? () => setzeDialog(true) : undefined}
        />
      ) : leerArt === "filter" ? (
        <Leerzustand
          titel="Kein Projekt passt zur Suche."
          hinweis="Filter löschen, um alle Projekte zu sehen."
          aktionLabel="Filter löschen"
          onAktion={() => setzeQ("")}
        />
      ) : (
        <div
          className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3"
          data-testid="projekte-grid"
        >
          {gefiltert.map((p) => {
            const rolle = rollen.get(p.id);
            return (
              <Link
                key={p.id}
                to="/projekte/$id"
                params={{ id: p.id }}
                search={{ reiter: "aufgaben" }}
                className="block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <Card className="h-full p-5 transition-shadow duration-150 ease-aus hover:shadow-md">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="size-[9px] flex-none rounded-full"
                        style={{ background: projektfarbe(p.kuerzel) }}
                        aria-hidden="true"
                      />
                      <h2 className="truncate text-[15.5px] font-bold text-gray-900 dark:text-gray-50">
                        {p.name}
                      </h2>
                    </div>
                    {rolle ? <Badge variant="neutral">{rolle}</Badge> : null}
                  </div>
                  {p.beschreibung ? (
                    <p className="mt-2 line-clamp-2 text-[13px] leading-relaxed text-gray-600 dark:text-gray-400">
                      {p.beschreibung}
                    </p>
                  ) : null}
                  {/* Im Entwurf steht hier die Repo-Adresse in Monospace. Die
                      gibt es nicht — die Kennung des Projekts schon, und sie
                      erfüllt denselben Zweck: die technische Kurzform. */}
                  <p className="mt-3 font-mono text-[11.5px] text-gray-500">
                    {p.kuerzel}
                  </p>
                </Card>
              </Link>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOffen} onOpenChange={setzeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neues Projekt</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            <div>
              <Label htmlFor="kuerzel">Kürzel</Label>
              <Input
                id="kuerzel"
                value={kuerzel}
                onChange={(e) => setzeKuerzel(e.target.value)}
                placeholder="CUPR"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setzeName(e.target.value)}
                className="mt-1"
              />
            </div>
            {fehler ? (
              <Fehlerzeile meldung={{ art: "sonstiges", text: fehler, eingabeBehalten: true, neuLadenAnbieten: false, erneutVersuchen: false }} />
            ) : null}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => setzeDialog(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => void speichern()}
              disabled={!kuerzel.trim() || !name.trim() || anlegen.isPending}
            >
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
